/**
 * FERP Portal - Practice Survey Runner
 *
 * LIFE-CRITICAL: Step-through mock DHS survey walkthrough.
 * Presents rules by category, accepts evidence uploads,
 * generates pass/fail report per rule.
 *
 * Driven by practice survey atomics (wf-4-3-002 through wf-4-3-005).
 * Data from regulatoryRules query filtered by category tags.
 *
 * Pattern source: RegulatoryRulesPage (table + drawer)
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  Stepper,
  SimpleGrid,
  Loader,
  Button,
  Progress,
  Paper,
  Divider,
  Textarea,
} from '@mantine/core';
import {
  IconShieldCheck,
  IconAlertTriangle,
  IconInfoCircle,
  IconCheck,
  IconX,
  IconClipboardCheck,
  IconUsers,
  IconBook,
  IconFileText,
  IconAlertOctagon,
  IconPlayerPlay,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import {
  useListRegulatoryRulesQuery,
  GqlSafetyLevel,
  GqlRuleStatus,
  type RegulatoryRuleFieldsFragment,
} from '@/generated/graphql';
import { SAFETY_BADGE_CONFIG } from '@/config/regulatory-status';

type SurveyCategory = 'staffing' | 'training' | 'documentation' | 'incidents';

interface RuleVerdict {
  ruleId: string;
  passed: boolean;
  notes: string;
}

const CATEGORIES: { key: SurveyCategory; label: string; icon: React.ReactNode; tags: string[] }[] = [
  { key: 'staffing', label: 'Staffing', icon: <IconUsers size={18} />, tags: ['staff_qualifications', 'background_checks'] },
  { key: 'training', label: 'Training', icon: <IconBook size={18} />, tags: ['training', 'orientation', 'competency'] },
  { key: 'documentation', label: 'Documentation', icon: <IconFileText size={18} />, tags: ['care_plan', 'timesheet', 'documentation'] },
  { key: 'incidents', label: 'Incidents', icon: <IconAlertOctagon size={18} />, tags: ['incident_reporting', 'maltreatment'] },
];

export function PracticeSurveyRunner() {
  const tenantId = useTenantId();
  const skip = !tenantId;
  const [surveyStarted, setSurveyStarted] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [currentRuleIndex, setCurrentRuleIndex] = useState(0);
  const [verdicts, setVerdicts] = useState<Record<string, RuleVerdict>>({});
  const [currentNotes, setCurrentNotes] = useState('');

  const { data, loading } = useListRegulatoryRulesQuery({
    variables: {
      filter: {
        tenantId: tenantId ?? '',
        status: GqlRuleStatus.APPROVED,
        limit: 200,
      },
    },
    skip: skip || !surveyStarted,
  });

  const allRules = useMemo(() => data?.regulatoryRules ?? [], [data]);

  // Group rules by category based on tags
  const rulesByCategory = useMemo(() => {
    const grouped: Record<SurveyCategory, RegulatoryRuleFieldsFragment[]> = {
      staffing: [],
      training: [],
      documentation: [],
      incidents: [],
    };

    for (const rule of allRules) {
      const tags = rule.classification.tags ?? [];
      for (const cat of CATEGORIES) {
        if (cat.tags.some((t) => tags.includes(t))) {
          grouped[cat.key].push(rule);
          break;
        }
      }
    }

    return grouped;
  }, [allRules]);

  const currentCategory = CATEGORIES[activeStep];
  const currentRules = currentCategory ? rulesByCategory[currentCategory.key] : [];
  const currentRule = currentRules[currentRuleIndex] ?? null;

  const categoryVerdicts = useCallback(
    (category: SurveyCategory) => {
      const rules = rulesByCategory[category];
      const judged = rules.filter((r) => verdicts[r.id]);
      const passed = rules.filter((r) => verdicts[r.id]?.passed);
      return { total: rules.length, judged: judged.length, passed: passed.length };
    },
    [rulesByCategory, verdicts],
  );

  const handleVerdict = useCallback(
    (passed: boolean) => {
      if (!currentRule) return;
      setVerdicts((prev) => ({
        ...prev,
        [currentRule.id]: { ruleId: currentRule.id, passed, notes: currentNotes },
      }));
      setCurrentNotes('');

      // Advance to next rule or next category
      if (currentRuleIndex < currentRules.length - 1) {
        setCurrentRuleIndex((i) => i + 1);
      } else if (activeStep < CATEGORIES.length - 1) {
        setActiveStep((s) => s + 1);
        setCurrentRuleIndex(0);
      }
    },
    [currentRule, currentRuleIndex, currentRules.length, activeStep, currentNotes],
  );

  const totalRules = Object.values(rulesByCategory).reduce((sum, arr) => sum + arr.length, 0);
  const totalJudged = Object.keys(verdicts).length;
  const totalPassed = Object.values(verdicts).filter((v) => v.passed).length;
  const surveyComplete = totalJudged >= totalRules && totalRules > 0;

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconInfoCircle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to run practice survey.
        </Alert>
      </Container>
    );
  }

  if (!surveyStarted) {
    return (
      <Container size="md" py="xl">
        <Card withBorder p="xl">
          <Stack align="center" gap="lg">
            <ThemeIcon variant="light" size={80} radius="xl" color="blue">
              <IconClipboardCheck size={40} />
            </ThemeIcon>
            <Title order={2}>Practice Survey</Title>
            <Text c="dimmed" ta="center" maw={500}>
              Walk through regulatory rules by category as if a DHS surveyor
              were conducting an on-site audit. Answer for each rule, upload
              evidence, and get a pass/fail readiness report.
            </Text>
            <SimpleGrid cols={2}>
              {CATEGORIES.map((cat) => (
                <Card key={cat.key} withBorder p="sm">
                  <Group gap="xs">
                    <ThemeIcon variant="light" size="sm">{cat.icon}</ThemeIcon>
                    <Text size="sm" fw={500}>{cat.label}</Text>
                  </Group>
                </Card>
              ))}
            </SimpleGrid>
            <Button
              size="lg"
              leftSection={<IconPlayerPlay size={20} />}
              onClick={() => setSurveyStarted(true)}
            >
              Start Practice Survey
            </Button>
          </Stack>
        </Card>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Practice Survey</Title>
            <Text c="dimmed" size="sm">
              {totalJudged}/{totalRules} rules reviewed
            </Text>
          </div>
          <Progress
            value={totalRules > 0 ? (totalJudged / totalRules) * 100 : 0}
            color="blue"
            size="lg"
            w={200}
          />
        </Group>

        {loading ? (
          <Group justify="center" py="xl">
            <Loader size="lg" />
          </Group>
        ) : surveyComplete ? (
          /* Survey Complete — Results */
          <Stack gap="md">
            <Alert
              icon={<IconCheck size={16} />}
              color={totalPassed === totalRules ? 'green' : 'yellow'}
              title="Practice Survey Complete"
            >
              {totalPassed}/{totalRules} rules passed.
              {totalPassed === totalRules
                ? ' Excellent — fully survey-ready!'
                : ` ${totalRules - totalPassed} rules need attention before the real survey.`}
            </Alert>

            {/* Category Results */}
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              {CATEGORIES.map((cat) => {
                const cv = categoryVerdicts(cat.key);
                const pct = cv.total > 0 ? Math.round((cv.passed / cv.total) * 100) : 0;
                return (
                  <Card key={cat.key} withBorder p="md">
                    <Group justify="space-between">
                      <Group gap="xs">
                        <ThemeIcon variant="light" size="sm">{cat.icon}</ThemeIcon>
                        <Text fw={500}>{cat.label}</Text>
                      </Group>
                      <Badge
                        color={pct === 100 ? 'green' : pct >= 70 ? 'yellow' : 'red'}
                        variant="light"
                      >
                        {cv.passed}/{cv.total} passed
                      </Badge>
                    </Group>
                    <Progress
                      value={pct}
                      color={pct === 100 ? 'green' : pct >= 70 ? 'yellow' : 'red'}
                      size="sm"
                      mt="sm"
                    />
                  </Card>
                );
              })}
            </SimpleGrid>
          </Stack>
        ) : (
          /* Active Survey */
          <>
            {/* Category Stepper */}
            <Stepper active={activeStep} onStepClick={setActiveStep} size="sm">
              {CATEGORIES.map((cat) => {
                const cv = categoryVerdicts(cat.key);
                return (
                  <Stepper.Step
                    key={cat.key}
                    label={cat.label}
                    description={`${cv.judged}/${cv.total}`}
                    icon={cat.icon}
                    completedIcon={<IconCheck size={18} />}
                  />
                );
              })}
            </Stepper>

            {/* Current Rule */}
            {currentRule ? (
              <Card withBorder p="lg">
                <Stack gap="md">
                  <Group justify="space-between">
                    <Group gap="xs">
                      <Text fw={600}>{currentRule.id}</Text>
                      <Badge
                        color={
                          (SAFETY_BADGE_CONFIG[currentRule.classification.safetyLevel]
                            ?? { color: 'gray' }).color
                        }
                        variant="light"
                        size="sm"
                      >
                        {currentRule.classification.safetyLevel}
                      </Badge>
                    </Group>
                    <Text size="sm" c="dimmed">
                      Rule {currentRuleIndex + 1} of {currentRules.length}
                    </Text>
                  </Group>

                  <Divider />

                  {/* Surveyor Question */}
                  <Paper withBorder p="md" bg="blue.0">
                    <Text size="sm" fw={500} mb="xs">Surveyor Question:</Text>
                    <Text size="sm">
                      Can you demonstrate that <strong>{currentRule.structured.actor}</strong> performs{' '}
                      <strong>{currentRule.structured.action}</strong> when{' '}
                      <strong>{currentRule.structured.trigger}</strong>?
                      {currentRule.structured.deadline && (
                        <> (within <strong>{currentRule.structured.deadline}</strong>)</>
                      )}
                    </Text>
                  </Paper>

                  {/* Rule Text */}
                  <Text size="sm" c="dimmed">{currentRule.text}</Text>

                  {/* Evidence Required */}
                  {currentRule.structured.evidence.length > 0 && (
                    <Paper withBorder p="sm">
                      <Text size="xs" fw={500} mb="xs">Required Evidence:</Text>
                      {currentRule.structured.evidence.map((ev) => (
                        <Group key={ev.name} gap="xs" mb={4}>
                          <Badge
                            size="xs"
                            color={ev.required ? 'red' : 'gray'}
                            variant="light"
                          >
                            {ev.required ? 'Required' : 'Optional'}
                          </Badge>
                          <Text size="xs">{ev.name}: {ev.description}</Text>
                        </Group>
                      ))}
                    </Paper>
                  )}

                  {/* Notes */}
                  <Textarea
                    label="Notes"
                    placeholder="Observations, evidence location, follow-up items..."
                    value={currentNotes}
                    onChange={(e) => setCurrentNotes(e.currentTarget.value)}
                    autosize
                    minRows={2}
                    maxRows={4}
                  />

                  {/* Verdict Buttons */}
                  <Group justify="center" gap="lg">
                    <Button
                      color="green"
                      size="md"
                      leftSection={<IconCheck size={18} />}
                      onClick={() => handleVerdict(true)}
                    >
                      Pass
                    </Button>
                    <Button
                      color="red"
                      size="md"
                      leftSection={<IconX size={18} />}
                      onClick={() => handleVerdict(false)}
                    >
                      Fail
                    </Button>
                  </Group>
                </Stack>
              </Card>
            ) : (
              <Text c="dimmed" ta="center" py="xl">
                No rules found for this category.
              </Text>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
}

export default PracticeSurveyRunner;
